// @ts-check
/**
 * Shared CDM ReferencesView Component
 *
 * Renders references for every CDM type (CRM, HRIS, Finance, Procurement)
 * using each type's display_spec served by ToolsV2Metadata. The spec maps
 * enriched-view column name → FieldDisplayInfo ({ display_label, priority,
 * section, display }). Fields whose priority is "primary" / "secondary" /
 * "tertiary" become the (up to) three columns of the compact table, in that
 * order; sys_identifier / other / null priorities are not shown here. Header
 * text comes straight from display_label — no client-side humanization.
 *
 * The type name is shown as a title row above the column headers.
 *
 * When no field has priority "primary", the primary cell falls back to the
 * reference's display_identifier (which the backend formats from the type's
 * display_template). That column's header is left blank because the value is a
 * composed string, not a single named column.
 *
 * Cell values are pretty-printed with light heuristics — ISO dates use
 * formatDate, booleans render as Yes/No, numbers above 100 get
 * thousand-separators. No per-type code paths.
 */

/** @typedef {import('../../../../stores/referenceUnitStore').ReferenceUnitHandle} ReferenceUnitHandle */
/** @typedef {import('../../../../stores/referenceUnitStore').DisplaySlot} DisplaySlot */
/** @typedef {import('../../../../stores/referenceUnitStore').FieldDisplayInfo} FieldDisplayInfo */

const SLOTS = /** @type {const} */ (['primary', 'secondary', 'tertiary']);

/**
 * @param {any} deps
 */
export default function createComponent(deps) {
    const {
        computed,
        inject,
        useDataViewStore,
        useReferenceUnitStore,
        RawViewContainer,
        ReferenceRowTags,
        formatDate,
        useRowSelection,
    } = deps;

    return {
        name: 'CdmReferencesView',

        components: {
            RawViewContainer,
            ReferenceRowTags,
        },

        props: {
            references: {
                type: Array,
                required: true,
            },
            question: {
                type: String,
                required: true,
            },
        },

        /** @param {{ references: ReferenceUnitHandle[], question: string }} props */
        setup(props) {
            const dataViewStore = useDataViewStore();
            const referenceUnitStore = useReferenceUnitStore();
            const openDetailView = inject('openDetailView', null);

            /** @param {ReferenceUnitHandle} reference */
            const handleRowClick = (reference) => {
                const refIndex = props.references.findIndex(
                    /** @param {ReferenceUnitHandle} r */
                    (r) => r.content_hash === reference.content_hash,
                );

                if (refIndex !== -1) {
                    dataViewStore.communicationGroups = props.references;
                    dataViewStore.currentGroupIndex = refIndex;

                    if (openDetailView) {
                        openDetailView();
                    } else {
                        dataViewStore.openCommunicationRawView(props.references, refIndex);
                    }
                }
            };

            /** @param {ReferenceUnitHandle} ref */
            const isRowSelected = (ref) => {
                const currentRef =
                    dataViewStore.communicationGroups[dataViewStore.currentGroupIndex];
                return currentRef && currentRef.content_hash === ref.content_hash;
            };

            /**
             * Pretty-print a single enriched-view cell.
             *
             * Heuristics:
             * - Date-looking ISO strings → formatDate.
             * - Booleans → Yes/No.
             * - Numbers larger than 100 → thousands separators.
             * - null/undefined/empty string → '' (so the cell renders empty).
             *
             * @param {unknown} val
             */
            const formatValue = (val) => {
                if (val === null || val === undefined || val === '') return '';
                if (typeof val === 'boolean') return val ? 'Yes' : 'No';
                if (typeof val === 'number') {
                    return Math.abs(val) > 100 ? val.toLocaleString() : String(val);
                }
                if (typeof val === 'string') {
                    if (/^\d{4}-\d{2}-\d{2}/.test(val)) {
                        const formatted = formatDate(val);
                        return formatted || val;
                    }
                    return val;
                }
                return String(val);
            };

            // The view always renders a single type's references, so the type
            // name and display_spec come from the first reference.
            const typeName = computed(() => {
                const first = props.references[0];
                return first ? first.type : '';
            });

            /**
             * Resolve the display_spec into an ordered list of column
             * descriptors for the three visible slots. Each descriptor carries
             * the slot, the backing column name (null for a composed-primary
             * fallback), the header label (from display_label), and an inline
             * width %.
             *
             * Slots whose column is absent from the spec are dropped, so a type
             * with only primary/secondary yields a two-column table. Widths are
             * split evenly across whatever columns survive, except primary,
             * which stays width-less so it absorbs the table width left over
             * after the fixed-width checkbox column (table-layout: fixed
             * fattens every column, the checkbox one included, when the
             * specified widths under- or over-shoot the table width).
             */
            const columns = computed(() => {
                const spec = referenceUnitStore.getDisplaySpec(typeName.value) || {};

                /** @type {Record<string, { column: string, label: string }>} */
                const slotToColumn = {};
                for (const [colName, info] of Object.entries(spec)) {
                    if (info.display === false) continue;
                    const priority = info.priority;
                    if (priority && SLOTS.includes(/** @type {any} */ (priority))) {
                        slotToColumn[priority] = {
                            column: colName,
                            label: info.display_label || colName,
                        };
                    }
                }

                /** @type {{ slot: string, column: string | null, label: string }[]} */
                const cols = [];
                for (const slot of SLOTS) {
                    const entry = slotToColumn[slot];
                    if (slot === 'primary') {
                        // Primary may be absent (composed display_template). Keep
                        // the column so display_identifier still has a home, but
                        // leave the header blank since it's a composed string.
                        cols.push({
                            slot,
                            column: entry ? entry.column : null,
                            label: entry ? entry.label : '',
                        });
                    } else if (entry) {
                        cols.push({ slot, column: entry.column, label: entry.label });
                    }
                }

                const widthPct = cols.length ? `${(100 / cols.length).toFixed(4)}%` : '100%';
                return cols.map((c) => ({
                    ...c,
                    width: c.slot === 'primary' ? null : widthPct,
                }));
            });

            /**
             * Cell value for a reference under a given column descriptor.
             * Primary with no backing column falls back to display_identifier.
             *
             * @param {ReferenceUnitHandle} ref
             * @param {{ slot: string, column: string | null }} col
             */
            const cellValue = (ref, col) => {
                const m = ref.metadata || {};
                if (col.column) {
                    const formatted = formatValue(m[col.column]);
                    if (col.slot === 'primary') {
                        return formatted || ref.display_identifier || ref.type;
                    }
                    return formatted;
                }
                // Composed-primary fallback.
                return ref.display_identifier || ref.type;
            };

            // Bulk-selection checkboxes + session-only tag display. Shared
            // behavior lives in useRowSelection / referenceSelectionStore.
            const rowSelection = useRowSelection(() => props.references);

            return {
                handleRowClick,
                isRowSelected,
                cellValue,
                typeName,
                columns,
                openDetailView,
                selAllChecked: rowSelection.allChecked,
                selSomeChecked: rowSelection.someChecked,
                toggleSelectAll: rowSelection.toggleAll,
                isRowChecked: rowSelection.isChecked,
                toggleRowChecked: rowSelection.toggleChecked,
                showCommunicationRawView: computed(() => dataViewStore.showCommunicationRawView),
                communicationGroups: computed(() => dataViewStore.communicationGroups),
                currentGroupIndex: computed(() => dataViewStore.currentGroupIndex),
                closeCommunicationRawView: () => dataViewStore.closeCommunicationRawView(),
            };
        },

        template: `
            <div class="conversation-references-view cdm-references-view">
                <div class="crv-table-container">
                    <table class="crv-table cdm-table">
                        <colgroup>
                            <col class="crv-select-col" />
                            <col
                                v-for="col in columns"
                                :key="col.slot"
                                :style="{ width: col.width }"
                            />
                        </colgroup>
                        <thead>
                            <tr class="cdm-type-title-row">
                                <th :colspan="(columns.length || 1) + 1">{{ typeName }}</th>
                            </tr>
                            <tr class="cdm-column-header-row">
                                <th class="crv-select-cell" @click.stop>
                                    <input
                                        type="checkbox"
                                        class="crv-row-checkbox"
                                        title="Select all"
                                        :checked="selAllChecked"
                                        :indeterminate="selSomeChecked"
                                        @change="toggleSelectAll"
                                    />
                                </th>
                                <th
                                    v-for="col in columns"
                                    :key="col.slot"
                                    :style="{ width: col.width }"
                                    class="cdm-column-header"
                                >
                                    {{ col.label }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="ref in references"
                                :key="ref.content_hash"
                                :class="{ 'row-selected': isRowSelected(ref) }"
                                @click="handleRowClick(ref)"
                                class="cdm-row"
                            >
                                <td class="crv-select-cell" @click.stop>
                                    <input
                                        type="checkbox"
                                        class="crv-row-checkbox"
                                        :checked="isRowChecked(ref)"
                                        @change="toggleRowChecked(ref)"
                                    />
                                </td>
                                <td
                                    v-for="col in columns"
                                    :key="col.slot"
                                    :class="'cdm-cell cdm-cell-' + col.slot"
                                >
                                    <span
                                        v-if="col.slot === 'primary'"
                                        class="crv-conversation-link"
                                    >{{ cellValue(ref, col) }}</span>
                                    <span v-else>{{ cellValue(ref, col) }}</span>
                                    <ReferenceRowTags
                                        v-if="col.slot === 'primary'"
                                        :content-hash="ref.content_hash"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div v-if="references.length === 0" class="crv-empty">
                    No records found.
                </div>

                <RawViewContainer
                    v-if="!openDetailView && communicationGroups.length > 0 && showCommunicationRawView"
                    :references="communicationGroups"
                    :initialIndex="currentGroupIndex"
                    :visible="showCommunicationRawView"
                    @close="closeCommunicationRawView"
                />
            </div>
        `,
    };
}
